/**
 * SRES · Enterprise 8-Step Create Project Wizard v3 (Sprint P37y · Feb 2026)
 *
 *  Step 1  Identity & Location        — name, client, country, state, coords
 *  Step 2  Commodity & Geology        — commodity, deposit type, mineralisation
 *  Step 3  CRS & Boundary             — EPSG, UTM, boundary upload
 *  Step 4  Data & Exploration         — file uploads (all 6 kinds)
 *  Step 5  Modelling & Estimation     — density, blocks, search radius, top-cut
 *  Step 6  Classification & Economics — classification, cutoff, price, costs
 *  Step 7  Team & Reporting           — CP, reporting standard, effective date
 *  Step 8  AI Review & Create         — review + one-click create + AI suggestions
 *
 * Every value is stored inside a versioned ProjectConfiguration on
 * the backend (routes/project_configuration.py) so the wizard and the
 * ProjectSetupCommandCenter always see the same document.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ArrowLeft, ArrowRight, CheckCircle, CloudArrowUp,
  RocketLaunch, Sparkle, Warning, X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

import Step1Identity        from "./steps/Step1Identity";
import Step2Commodity        from "./steps/Step2Commodity";
import Step3CRSBoundary      from "./steps/Step3CRSBoundary";
import Step4Data             from "./steps/Step4Data";
import Step5Modelling        from "./steps/Step5Modelling";
import Step6Classification    from "./steps/Step6Classification";
import Step7Team              from "./steps/Step7Team";
import Step8AIReview          from "./steps/Step8AIReview";

const DRAFT_KEY = "sres.create-project.v3.draft";

const STEPS = [
  { id: 1, key: "identity",       label: "Identity & Location",     required: ["name", "country"] },
  { id: 2, key: "commodity",      label: "Commodity & Geology",     required: ["commodity"] },
  { id: 3, key: "crs",             label: "CRS & Boundary",          required: [] },
  { id: 4, key: "data",            label: "Data & Uploads",          required: [] },
  { id: 5, key: "modelling",       label: "Modelling & Estimation",  required: [] },
  { id: 6, key: "classification", label: "Classification & Economics", required: [] },
  { id: 7, key: "team",            label: "Team & Reporting",        required: ["reporting_standard"] },
  { id: 8, key: "review",          label: "AI Review & Create",      required: [] },
];

function loadDraft() {
  try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || "{}") || {}; }
  catch { return {}; }
}
function persistDraft(form) {
  try { localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch { /* quota */ }
}

export default function CreateProjectWizard() {
  const nav = useNavigate();

  const [form, setForm]     = useState(() => ({
    reporting_standard: "JORC_2012",
    project_stage:      "early_exploration",
    ...loadDraft(),
  }));
  const [step, setStep]     = useState(1);
  const [saving, setSaving] = useState(false);
  const [uploadsStatus, setUploadsStatus] = useState(null);

  const setPartial = useCallback((patch) => setForm((p) => ({ ...p, ...patch })), []);
  useEffect(() => { persistDraft(form); }, [form]);

  // ── Backend draft creation (idempotent) ──────────────────────────
  const ensureDraftMut = useMutation({
    mutationFn: async () => {
      if (form.draft_project_id) return { draft_project_id: form.draft_project_id };
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== undefined));
      const r = await api.post("/setup/draft", payload);
      return r.data;
    },
    onSuccess: (r) => {
      if (r?.draft_project_id && !form.draft_project_id) {
        setPartial({ draft_project_id: r.draft_project_id,
                     project_code:     r.project?.project_code,
                     company:          r.project?.company });
      }
    },
  });

  // ── Configuration read (once draft exists) ────────────────────────
  const cfgQ = useQuery({
    queryKey: ["config", form.draft_project_id],
    enabled: Boolean(form.draft_project_id),
    queryFn: () => api.get(`/projects/${form.draft_project_id}/configuration`).then((r) => r.data.configuration),
    refetchInterval: false,
  });

  const patchCfgMut = useMutation({
    mutationFn: (patches) =>
      api.patch(`/projects/${form.draft_project_id}/configuration`, { patches }).then((r) => r.data),
    onSuccess: () => cfgQ.refetch(),
  });

  const aiPreviewMut = useMutation({
    mutationFn: (overrides) =>
      api.post(`/projects/${form.draft_project_id}/configuration/ai-preview`, overrides || {}).then((r) => r.data),
  });

  const aiApplyMut = useMutation({
    mutationFn: (overrides) =>
      api.post(`/projects/${form.draft_project_id}/configuration/ai-apply`, overrides || {}).then((r) => r.data),
    onSuccess: (r) => {
      toast.success(`Applied ${r.count} AI suggestions`);
      cfgQ.refetch();
    },
  });

  // Stabilise callbacks so useEffect deps in children don't loop.
  const aiPreview = useCallback((o) => aiPreviewMut.mutateAsync(o), [aiPreviewMut]);
  const aiApply   = useCallback((o) => aiApplyMut.mutateAsync(o),   [aiApplyMut]);
  const patchCfg  = useCallback((p) => patchCfgMut.mutateAsync(p),   [patchCfgMut]);
  const ensureDraft = useCallback(() => ensureDraftMut.mutateAsync(), [ensureDraftMut]);

  // ── Actions ───────────────────────────────────────────────────────
  const goPrev = useCallback(() => setStep((s) => Math.max(1, s - 1)), []);
  const goNext = useCallback(async () => {
    const spec = STEPS[step - 1];
    const missing = (spec.required || []).filter((k) => !form[k]);
    if (missing.length > 0) {
      toast.error(`Please fill ${missing.map((m) => m.replace(/_/g, " ")).join(", ")} before continuing.`);
      return;
    }
    // On leaving step 1: create the backend draft if we haven't yet
    if (step === 1 && !form.draft_project_id) {
      try { await ensureDraftMut.mutateAsync(); } catch (e) {
        toast.error("Could not create draft — try again.");
        return;
      }
    }
    setStep((s) => Math.min(STEPS.length, s + 1));
    document.getElementById("wizard-body")?.scrollTo({ top: 0 });
  }, [step, form, ensureDraftMut]);

  const goStep = useCallback((n) => {
    if (n <= step) return setStep(n);
    // Forward navigation must respect required-field gates.
    for (let i = 1; i < n; i++) {
      const missing = (STEPS[i - 1].required || []).filter((k) => !form[k]);
      if (missing.length > 0) { toast.error("Complete earlier steps first."); return; }
    }
    setStep(n);
  }, [step, form]);

  const saveDraft = useCallback(async () => {
    setSaving(true);
    persistDraft(form);
    if (form.draft_project_id) {
      try {
        const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== undefined));
        await api.patch(`/setup/draft/${form.draft_project_id}`, payload);
      } catch { /* silent */ }
    }
    setSaving(false);
    toast.success("Draft saved.");
  }, [form]);

  const createMut = useMutation({
    mutationFn: () => api.post("/setup/create", form).then((r) => r.data),
    onSuccess:  (r) => {
      toast.success("Project created — opening Command Center…");
      localStorage.removeItem(DRAFT_KEY);
      nav(`/app/projects/${r.project_id}/setup`);
    },
    onError: (e) => toast.error(
      e?.response?.data?.detail?.message
      || e?.response?.data?.detail
      || "Create failed"
    ),
  });

  const canCreate = Boolean(form.name && form.country && form.commodity && form.reporting_standard);
  const isLastStep = step === STEPS.length;

  const stepContext = {
    form, set: setPartial,
    cfg: cfgQ.data,
    patchCfg,
    aiPreview,
    aiApply,
    aiPreviewing: aiPreviewMut.isPending,
    aiApplying:   aiApplyMut.isPending,
    aiPreviewResult: aiPreviewMut.data,
    ensureDraft,
    draftId: form.draft_project_id,
    uploadsStatus,
    onUploadsChange: setUploadsStatus,
  };

  return (
    <div className="wizard-shell h-full min-h-0 flex flex-col bg-slate-50 overflow-hidden"
             data-testid="create-project-wizard">
      {/* ─── HEADER · two rows to prevent overlap with the 8-step pills ─── */}
      <header className="shrink-0 bg-white border-b border-slate-200"
                     style={{ zIndex: 30 }}>
        <div className="px-6 pt-3 pb-2 flex items-center gap-4">
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.28em] text-slate-500">
              New Project · Setup Wizard
            </div>
            <h1 className="font-heading font-black text-xl tracking-tight text-slate-900 leading-tight truncate">
              Create New Project
            </h1>
          </div>
          <Button variant="ghost" size="sm" onClick={() => nav("/app")}
                     className="gap-1.5 text-[11.5px]" data-testid="btn-cancel">
            <X size={12} /> Cancel
          </Button>
        </div>
        <div className="px-6 pb-3 pt-1 border-t border-slate-100 bg-slate-50/70 overflow-x-auto">
          <StepProgress step={step} onStep={goStep} />
        </div>
      </header>

      {/* ─── BODY (only scroll container) ─── */}
      <div
        id="wizard-body"
        className="flex-1 min-h-0 overflow-y-auto"
        style={{ paddingBottom: 40 }}
        data-testid={`wizard-step-${step}-body`}
      >
        <div className="max-w-5xl mx-auto px-6 py-6">
          {step === 1 && <Step1Identity        {...stepContext} />}
          {step === 2 && <Step2Commodity        {...stepContext} />}
          {step === 3 && <Step3CRSBoundary      {...stepContext} />}
          {step === 4 && <Step4Data             {...stepContext} />}
          {step === 5 && <Step5Modelling        {...stepContext} />}
          {step === 6 && <Step6Classification    {...stepContext} />}
          {step === 7 && <Step7Team              {...stepContext} />}
          {step === 8 && <Step8AIReview          {...stepContext} canCreate={canCreate} />}
        </div>
      </div>

      {/* ─── FOOTER ─── */}
      <footer className="shrink-0 bg-white border-t border-slate-200"
                    style={{ zIndex: 30 }}>
        <div className="px-6 py-3 flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={goPrev} disabled={step === 1}
                     className="gap-1.5 text-[11.5px]" data-testid="btn-back">
            <ArrowLeft size={12} /> Back
          </Button>
          <Button variant="outline" size="sm" onClick={saveDraft} disabled={saving}
                     className="gap-1.5 text-[11.5px]" data-testid="btn-save-draft">
            <CloudArrowUp size={12} /> {saving ? "Saving…" : "Save Draft"}
          </Button>
          <div className="flex-1 text-[11.5px] text-slate-500 text-center px-3">
            {step === 8 && !canCreate && (
              <span className="text-amber-700 inline-flex items-center gap-1">
                <Warning size={12} weight="fill" /> Fill <b>Name</b>, <b>Country</b>, <b>Commodity</b> and <b>Reporting Standard</b> to enable Create.
              </span>
            )}
            {step === 8 && canCreate && (
              <span className="text-emerald-700 inline-flex items-center gap-1">
                <CheckCircle size={12} weight="fill" /> Ready — click <b>Create Project</b>.
              </span>
            )}
            {step < STEPS.length && (
              <span>Step {step} of {STEPS.length} · <b>{STEPS[step - 1].label}</b></span>
            )}
          </div>
          {!isLastStep ? (
            <Button size="sm" onClick={goNext}
                       className="gap-1.5 text-[11.5px] bg-brand-blue hover:bg-brand-blue/90 text-white"
                       data-testid="btn-next">
              Next <ArrowRight size={12} />
            </Button>
          ) : (
            <Button size="sm" onClick={() => createMut.mutate()}
                       disabled={!canCreate || createMut.isPending}
                       className="gap-1.5 text-[11.5px] bg-emerald-600 hover:bg-emerald-700 text-white"
                       data-testid="btn-create">
              <RocketLaunch size={12} />
              {createMut.isPending ? "Creating…" : "Create Project"}
            </Button>
          )}
        </div>
      </footer>
    </div>
  );
}

function StepProgress({ step, onStep }) {
  const statuses = useMemo(() => STEPS.map((s) =>
    s.id < step ? "complete" : s.id === step ? "current" : "pending"), [step]);
  return (
    <div className="hidden lg:flex items-center gap-1" data-testid="wizard-progress">
      {STEPS.map((s, i) => {
        const status = statuses[i];
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onStep(s.id)}
            data-testid={`step-pill-${s.id}`}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-widest transition-all
              ${status === "complete"
                ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                : status === "current"
                ? "border-brand-blue bg-brand-blue/10 text-brand-blue"
                : "border-slate-200 bg-slate-50 text-slate-500"}`}>
            <span className="w-4 h-4 rounded-full inline-flex items-center justify-center text-[9px] font-black"
                     style={{
                       background: status === "complete" ? "#10b981"
                                    : status === "current" ? "#1E6FFF"
                                    : "#cbd5e1",
                       color: "#fff",
                     }}>
              {status === "complete" ? <CheckCircle size={9} weight="fill" /> : s.id}
            </span>
            <span className="hidden xl:inline">{s.label}</span>
          </button>
        );
      })}
    </div>
  );
}
